import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAdminPvpCampaigns,
  fetchAdminCampaignStats,
  formatIsoDate,
} from '../../lib/pvpCampaignApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
  StatGrid,
  StatCard,
  StatLabel,
  StatValue,
} from '../../styles/adminStyles';

export default function CampaignListPage() {
  const { t, tEnum, language } = usePortalT();
  const [campaigns, setCampaigns] = useState([]);
  const [statsById, setStatsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchAdminPvpCampaigns();
        if (cancelled) return;
        setCampaigns(rows);
        const statsEntries = await Promise.all(
          rows.map(async (c) => {
            try {
              const s = await fetchAdminCampaignStats(c.id);
              return [c.id, s];
            } catch {
              return [c.id, null];
            }
          }),
        );
        if (!cancelled) {
          setStatsById(Object.fromEntries(statsEntries));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = campaigns.reduce(
    (acc, c) => {
      const s = statsById[c.id];
      if (s) {
        acc.participants += s.participantCount || 0;
        acc.matches += s.totalMatches || 0;
        acc.points += s.totalPointsAwarded || 0;
      }
      if (c.status === 'active') acc.active += 1;
      return acc;
    },
    { participants: 0, matches: 0, points: 0, active: 0 },
  );

  return (
    <div>
      <PageTitle>{t('campaigns.dashTitle')}</PageTitle>
      <PageSubtitle>{t('campaigns.dashSubtitle')}</PageSubtitle>

      <StatGrid>
        <StatCard>
          <StatLabel>{t('campaigns.totalEvents')}</StatLabel>
          <StatValue>{campaigns.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.active')}</StatLabel>
          <StatValue>{totals.active}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.totalParticipants')}</StatLabel>
          <StatValue>{totals.participants}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.totalMatches')}</StatLabel>
          <StatValue>{totals.matches}</StatValue>
        </StatCard>
      </StatGrid>

      <BtnRow style={{ marginTop: 0, marginBottom: 20 }}>
        <Link to="/admin/campaigns/new"><Btn $primary>{t('campaigns.newEvent')}</Btn></Link>
        <Btn type="button" onClick={() => window.location.reload()}>{t('common.refresh')}</Btn>
      </BtnRow>

      {error ? <ErrorText>{error}</ErrorText> : null}
      {loading ? <p>{t('common.loading')}</p> : null}

      {!loading && campaigns.length === 0 ? (
        <Card>{t('campaigns.emptyList')}</Card>
      ) : null}

      {!loading && campaigns.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table>
            <thead>
              <tr>
                <th>{t('campaigns.colEvent')}</th>
                <th>{t('campaigns.colStatus')}</th>
                <th>{t('campaigns.colParticipants')}</th>
                <th>{t('campaigns.colMatches')}</th>
                <th>{t('campaigns.colTime')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...campaigns]
                .sort((a, b) => String(b.startsAt).localeCompare(String(a.startsAt)))
                .map((c) => {
                  const s = statsById[c.id];
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight: 800 }}>{c.titleZh}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{c.slug}</div>
                      </td>
                      <td><Badge $status={c.status}>{tEnum('campaignStatus', c.status)}</Badge></td>
                      <td>{s?.participantCount ?? '—'}</td>
                      <td>{s?.totalMatches ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{formatIsoDate(c.startsAt, language === 'zh')}</td>
                      <td>
                        <Link to={`/admin/campaigns/${c.id}`}>{t('common.detail')}</Link>
                        {' · '}
                        <Link to={`/admin/campaigns/${c.id}/edit`}>{t('common.edit')}</Link>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
